import React, {memo, useCallback, useEffect, useState} from 'react';
import {FieldLabel, FieldWrapper, InputFeedback} from '../index';
import Show from '../../components/Show';
import cx from 'classnames';
import MaskedInput from 'react-text-mask';
import {arePropsEqual} from '../../util/ReactHelpers';
import {useInputFocus} from '../hooks';
import {useDebouncedCallback} from 'use-debounce';

export const TextField = memo( ( props ) => {
        const {
            id,
            name,
            type = 'text',
            label,
            labelAppend,
            append,
            prepend,
            hint,
            isFocused,
            mask,
            disabled,
            error,
            errorShown,
            tooltip,
            value,
            onChange,
            onBlur,
            className,
            labelClassName,
            groupClassName,
            inputClassName,
            children,
            debounceOnChange = false,
            ...restProps
        } = props;

        const [innerValue, setInnerValue] = useState( value );
        const {fieldRef} = useInputFocus( isFocused );

        useEffect( () => {
            if ( value ) {
                setInnerValue( value );
            }
            else {
                setInnerValue( '' );
            }
        }, [value] );

        const debouncedHandleOnChange = useDebouncedCallback(
            ( event ) => onChange( event ),
            200
        );

        const handleOnChange = useCallback( ( event ) => {
            event.persist();

            const newValue = event.currentTarget.value;
            setInnerValue( newValue );
            if ( onChange ) {
                debounceOnChange ? debouncedHandleOnChange( event ) : onChange( event );
            }
        }, [] );

        const groupClasses = cx( 'input-group', groupClassName, {
            'animated shake error': !!error || !!errorShown
        } );

        const inputClasses = cx(
            'input-field w-full',
            {disabled: disabled},
            inputClassName
        );

        return (
            <div className={cx( 'w-full', {disabled: disabled}, className )}>
                <FieldLabel
                    label={label}
                    append={labelAppend}
                    tooltip={tooltip}
                    id={id}
                    hint={hint}
                    error={error}
                    className={labelClassName}
                />

                <div className={groupClasses}>
                    <Show when={prepend}>
                        <div className="input-group-prepend">
                        <span className="input-group-text">{prepend}</span>
                        </div>
                    </Show>
                    <Show when={append}>
                        <div className="input-group-append">
                           <span className="input-group-text">{append}</span>
                       </div>
                    </Show>
                    <div className="input-group-input">
                        {mask
                         ? <FieldWrapper
                             name={name}
                             innerRef={( ref ) => (fieldRef.current = ref)}>
                             {() => (
                                 <MaskedInput
                                     tabIndex={0}
                                     mask={mask}
                                     type={type}
                                     id={id}
                                     name={name || id}
                                     className={inputClasses}
                                     onBlur={onBlur}
                                     onChange={handleOnChange}
                                     disabled={disabled}
                                     value={innerValue}
                                     {...restProps}>
                                     {children}
                                 </MaskedInput>
                             )}
                         </FieldWrapper>
                         : <FieldWrapper
                             tabIndex={0}
                             innerRef={( ref ) => (fieldRef.current = ref)}
                             type={type}
                             id={id}
                             name={name || id}
                             className={inputClasses}
                             onBlur={onBlur}
                             onChange={handleOnChange}
                             disabled={disabled}
                             value={innerValue}
                             {...restProps}>
                             {children}
                         </FieldWrapper>
                        }
                        </div>
                    </div>
                <InputFeedback error={error}/>
            </div>
        );
    },
    arePropsEqual( ['value',
        'label',
        'labelAppend',
        'type',
        'minlength',
        'maxlength',
        'min',
        'max',
        'size',
        'step',
        'disabled',
        'error',
        'errorShown',
        'isFocused',
        'className',
        'groupClassName',
        'inputClassName',
        'children',
        'debounceOnChange'
    ] ) );

TextField.masks = {
    phoneNumberUS: [
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/
    ]
};
